/** Dark card / list surface in LSC (`dark:bg-secondary-800`). */
export const LSC_DARK_SURFACE_HEX = '#1e293b';

/** WCAG AA minimum contrast for normal-sized text on the LSC dark surface. */
export const WCAG_AA_CONTRAST_MIN = 4.5;

/** Neutral accent when no school colors are available. */
export const TEAM_CHIP_FALLBACK_ACCENT = '#94a3b8';

export type AccentColorSource =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'fallback';

export type AccentColorResolution = {
  accentColor: string;
  source: AccentColorSource;
  contrastRatio: number;
};

type AccentCandidate = {
  source: AccentColorSource;
  hex: string;
};

function parseHexColor(hex: string): [number, number, number] | null {
  const normalized = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  return [
    Number.parseInt(normalized.slice(0, 2), 16) / 255,
    Number.parseInt(normalized.slice(2, 4), 16) / 255,
    Number.parseInt(normalized.slice(4, 6), 16) / 255,
  ];
}

function linearize(channel: number): number {
  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return null;
  }
  const [r, g, b] = rgb.map(linearize);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(foregroundHex: string, backgroundHex: string): number {
  const foreground = relativeLuminance(foregroundHex);
  const background = relativeLuminance(backgroundHex);
  if (foreground == null || background == null) {
    return 0;
  }
  const lighter = Math.max(foreground, background);
  const darker = Math.min(foreground, background);
  return (lighter + 0.05) / (darker + 0.05);
}

export function resolveTeamAccentColor(input: {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  tertiaryColor?: string | null;
  quaternaryColor?: string | null;
  surfaceHex?: string;
  minContrast?: number;
}): AccentColorResolution {
  const surfaceHex = input.surfaceHex ?? LSC_DARK_SURFACE_HEX;
  const minContrast = input.minContrast ?? WCAG_AA_CONTRAST_MIN;

  const candidates: AccentCandidate[] = [];
  const push = (source: AccentColorSource, hex?: string | null) => {
    if (!hex) {
      return;
    }
    candidates.push({ source, hex: hex.toUpperCase() });
  };

  push('primary', input.primaryColor);
  push('secondary', input.secondaryColor);
  push('tertiary', input.tertiaryColor);
  push('quaternary', input.quaternaryColor);

  if (candidates.length === 0) {
    return {
      accentColor: TEAM_CHIP_FALLBACK_ACCENT,
      source: 'fallback',
      contrastRatio: contrastRatio(TEAM_CHIP_FALLBACK_ACCENT, surfaceHex),
    };
  }

  for (const candidate of candidates) {
    const ratio = contrastRatio(candidate.hex, surfaceHex);
    if (ratio >= minContrast) {
      return {
        accentColor: candidate.hex,
        source: candidate.source,
        contrastRatio: ratio,
      };
    }
  }

  let best = candidates[0];
  let bestRatio = contrastRatio(best.hex, surfaceHex);
  for (const candidate of candidates.slice(1)) {
    const ratio = contrastRatio(candidate.hex, surfaceHex);
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
  }

  return {
    accentColor: best.hex,
    source: best.source,
    contrastRatio: bestRatio,
  };
}
